import React from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';

interface ModalProps {
  open: boolean;
  title?: string;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Clases extra para el velo.
   *
   * 🔑 **Existe por el portal.** Este componente se dibuja con `createPortal(..., document.body)`, así
   * que su contenido cuelga de `<body>` y **no es descendiente de la pantalla que lo abrió**. Todo lo
   * que dependa de heredar —los tokens de un tema aplicado a un subárbol, el tipo de letra, el
   * color— se queda fuera.
   *
   * Eso es lo que hacía que los modales de la sección Developer salieran con la tipografía y los
   * colores de la operación normal en medio de una pantalla negra y amarilla. Pasando
   * `overlayClassName="dylo-dev-tokens dylo-dev-modal"` el portal declara la paleta por su cuenta.
   *
   * 📌 Se expone como prop en lugar de que el componente detecte el tema solo: el modal no tiene por
   * qué saber qué temas existen, y quien lo abre sí sabe en qué pantalla está.
   */
  overlayClassName?: string;
}

const CloseIcon: React.FC = () => (
  <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

const Modal: React.FC<ModalProps> = ({
  open,
  title,
  onClose,
  children,
  footer,
  overlayClassName,
}) => {
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className={[styles.overlay, overlayClassName || ''].filter(Boolean).join(' ')}
      onMouseDown={onClose}
    >
      <div className={styles.panel} onMouseDown={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>{title}</h3>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Close">
            <CloseIcon />
          </button>
        </div>
        <div className={styles.body}>{children}</div>
        {footer && <div className={styles.footer}>{footer}</div>}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
